import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
  useEditResource,
} from "@planzone/view-models";
import { Safeguard } from "@scenes/safeguard";
import { ResourceAvatar } from "@common/resourceAvatar";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { Draggable } from "@common/dragAndDrop";
import { useModal } from "@root/client/modal";
import { ResourceTypes } from "@planzone/dto";
import { TagsList } from "./tagsList";
import { RoleSelection } from "./roleSelection";
import { Props } from "./types";
import styles from "./styles.scss";

function Resource({ projectResource, columns }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "resource",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    projectResource.project.uuid,
    "resource",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const { isRequestPending, editResource } = useEditResource(
    projectResource.resource,
  );
  const { profession, name } = projectResource.resource;

  const fixedColMap: {
    [key: string]: (canEditProjectResource: boolean) => React.ReactElement;
  } = {
    tags: () => (
      <div className={styles.tagsList}>
        <TagsList
          tags={projectResource.resource.tags}
          project={projectResource.project}
          removableId={projectResource.resource.uuid}
        />
      </div>
    ),
    role: (canEditProjectResource) => (
      <div className={styles.roleSelection}>
        {projectResource.resource.type !== ResourceTypes.MATERIAL && (
          <RoleSelection
            projectResource={projectResource}
            readOnly={!canEditProjectResource}
          />
        )}
      </div>
    ),
  };

  return (
    <Draggable
      className={styles.resource}
      type="MOVE_RESOURCE"
      data={projectResource}
    >
      <React.Fragment>
        <div
          className={styles.user}
          onClick={() =>
            pushView({
              data: projectResource,
              key: UseCases.editProjectResource,
            })
          }
        >
          <div className={styles.avatar}>
            <ResourceAvatar resource={projectResource.resource} />
          </div>
          <div>
            <p
              className={
                projectResource.deactivated
                  ? `${styles.name} ${styles.deactivated}`
                  : styles.name
              }
            >
              {name}
            </p>
            {profession && <p className={styles.title}>{profession}</p>}
          </div>
        </div>
        <Safeguard
          useCases={[
            UseCases.editProjectResource,
            UseCases.removeResourceFromProject,
          ]}
        >
          {({ canEditProjectResource }) => (
            <>
              {columns.map((element) => {
                const criteria = template.find(
                  ({ uuid }) => uuid === element.key,
                );

                return (
                  <div key={element.key} className={styles.col}>
                    {!!criteria ? (
                      <div className={styles.form}>
                        <SecondaryCriteriaForm
                          criteria={criteria}
                          value={
                            projectResource.resource.attributes
                              ? projectResource.resource.attributes[
                                  criteria.uuid
                                ] || ""
                              : ""
                          }
                          flexible
                          disabled={isRequestPending}
                          submit={(value) =>
                            editResource({
                              resource: projectResource.resource,
                              nextResource: {
                                ...projectResource.resource,
                                attributes: {
                                  ...projectResource.resource.attributes,
                                  [criteria.uuid]: value,
                                },
                              },
                            })
                          }
                        />
                      </div>
                    ) : !!fixedColMap[element.key] ? (
                      fixedColMap[element.key](canEditProjectResource)
                    ) : null}
                  </div>
                );
              })}
            </>
          )}
        </Safeguard>
        <div>
          <div className={styles.contextualMenu}>
            <SecureContextualMenu
              items={[
                {
                  name: t("membersList.contextualMenu.edit"),
                  useCase: UseCases.editProjectResource,
                  act: () =>
                    pushView({
                      key: UseCases.editProjectResource,
                      data: projectResource,
                    }),
                },
                {
                  name: t("membersList.contextualMenu.remove"),
                  useCase: UseCases.removeResourceFromProject,
                  act: () =>
                    pushView({
                      key: UseCases.removeResourceFromProject,
                      data: {
                        project: projectResource.project,
                        projectResource,
                      },
                    }),
                },
                {
                  name: t("membersList.contextualMenu.transfer"),
                  useCase: UseCases.transferProjectResourceAllocations,
                  act: () =>
                    pushView({
                      key: UseCases.transferProjectResourceAllocations,
                      data: {
                        project: projectResource.project,
                        projectResource,
                      },
                    }),
                },
                ...(projectResource.deactivated
                  ? [
                      {
                        name: t("membersList.contextualMenu.activate"),
                        useCase: UseCases.activateProjectResource,
                        act: () =>
                          pushView({
                            key: UseCases.activateProjectResource,
                            data: projectResource,
                          }),
                      },
                    ]
                  : [
                      {
                        name: t("membersList.contextualMenu.disable"),
                        useCase: UseCases.disableProjectResource,
                        act: () =>
                          pushView({
                            key: UseCases.disableProjectResource,
                            data: {
                              project: projectResource.project,
                              projectResource,
                            },
                          }),
                      },
                    ]),
              ]}
            />
          </div>
        </div>
      </React.Fragment>
    </Draggable>
  );
}

export { Resource };
